// Hooks de escritura de Administración · Empresa (T-17p9). La lectura es
// useMyOrganization (lib/api/queries.ts, GET /organizations/me), que ya
// trae nombre, RUT, logo y marcas; por eso aquí no hay query propia y
// cada mutación invalida esa clave: el Sidebar lee la misma respuesta
// para su logo y el selector de marcas de Equipo para su lista, así que
// ven el cambio sin recargar.
//
// El nombre que muestra el Sidebar sale de la sesión (JWT), no de esta
// query: cambia en el próximo refresh del token, no aquí.

import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type {
  MyOrganizationBrandCreateRequest,
  MyOrganizationBrandUpdateRequest,
  MyOrganizationUpdateBody,
} from "@bitacorapro/api-types";
import { queryKeys } from "../../../lib/api/queries";
import { empresaApi } from "./api";
import { uploadLogoFile, type LogoTarget } from "./logoUpload";

function invalidateEmpresa(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: queryKeys.myOrganization });
}

export function useUpdateDatosLegales() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MyOrganizationUpdateBody) => empresaApi.patch(body),
    onSuccess: () => invalidateEmpresa(qc),
  });
}

// Un solo hook para el logo de la empresa y el de una marca: el drawer es
// el mismo, parametrizado por `target`.
export function useUploadLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ target, file }: { target: LogoTarget; file: File }) =>
      uploadLogoFile(target, file),
    onSuccess: () => invalidateEmpresa(qc),
  });
}

export function useRemoveOrgLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => empresaApi.removeOrgLogo(),
    onSuccess: () => invalidateEmpresa(qc),
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MyOrganizationBrandCreateRequest) => empresaApi.createBrand(body),
    onSuccess: () => invalidateEmpresa(qc),
  });
}

export function useRenameBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, body }: { brandId: string; body: MyOrganizationBrandUpdateRequest }) =>
      empresaApi.renameBrand(brandId, body),
    onSuccess: () => invalidateEmpresa(qc),
  });
}
